/** 4.9 Tab bar: Home, Tournaments, Clubs, Friends, Shop (D-196: Shop back as a fifth tab; D-165 had swapped it for Tournaments). A floating bar over a fade (D-131); the active cell is a bead disc. */
import type { ReactElement } from "react";
import { Link, useLocation } from "react-router-dom";
import { Icon } from "./Icon";
import { useUnread } from "../lib/session";
import { useShopNew } from "../lib/shopNew";
import { formatNumber } from "../lib/format";

export type TabKey = "home" | "tournaments" | "clubs" | "friends" | "shop" | "you";
/** D-146: every inactive icon takes the same quiet colour; the active cell is the bead disc and counts carry the accent. */
const TABS: { key: TabKey; icon: string; label: string; to: string }[] = [
  { key: "home", icon: "house", label: "Home", to: "/" },
  { key: "tournaments", icon: "trophy", label: "Tournaments", to: "/tournaments" },
  { key: "clubs", icon: "crest", label: "Clubs", to: "/clubs" },
  { key: "friends", icon: "users", label: "Friends", to: "/friends" },
  { key: "shop", icon: "gift", label: "Shop", to: "/shop" },
];
/**
 * Request counts sit on the tab that holds them (D-064): Friends shows new friend requests, Clubs shows join requests
 * at clubs you run, on every screen. `dots` only adds the plain dot for things that live on that tab (You's notifications).
 */
/** The tab pages (D-085, D-196): their top is the app bar and the page name, not a back button. Shop is back as the fifth. */
export const TAB_ROOTS = ["/", "/tournaments", "/clubs", "/friends", "/shop"];
/**
 * D-171: the tab bar shows on every signed-in page except the table and its lobby and results, the round review,
 * lessons, sign-in and admin, so Home is always one tap away. (D-085 kept it to the four tab pages.)
 */
const NO_TABS = /^\/(table|lobby|results|review|learn\/|signin|create-account|admin|dev|prize-checks|transfer)(\/|$)?/;
export const isTabRoot = (pathname: string): boolean => !NO_TABS.test(pathname);
/** The tab that a page belongs to, for lighting its cell. */
export function tabFor(pathname: string): TabKey | null {
  const p = pathname.replace(/\/+$/, "") || "/";
  if (p === "/" || /^\/(leaderboard|setup|practice|learn)$/.test(p)) return "home";
  if (p.startsWith("/tournaments")) return "tournaments";
  if (p.startsWith("/clubs")) return "clubs";
  if (/^\/(friends|players|people)(\/|$)/.test(p)) return "friends";
  if (p === "/shop" || p.startsWith("/occasion/")) return "shop";
  return null;
}

/** Pages still render `<TabBar active=… />`, but since D-171 the shell draws the one bar (`global`), so those are no-ops. */
export function TabBar({ active: _active, dots = {}, global = false }: { active?: TabKey; dots?: Partial<Record<TabKey, boolean>>; global?: boolean }): ReactElement | null {
  const unread = useUnread();
  const { pathname } = useLocation();
  if (!global || !isTabRoot(pathname)) return null;
  const active = tabFor(pathname);
  const counts: Partial<Record<TabKey, number>> = { friends: unread.friends, clubs: unread.clubs };
  const shopNew = useShopNew();
  const quiet: Partial<Record<TabKey, boolean>> = { ...dots, shop: dots.shop ?? shopNew };
  return (
    <>
    <div className="tabbar-fade" aria-hidden="true" />
    <nav className="tabbar" aria-label="Main">
      {TABS.map((t) => {
        const on = t.key === active;
        const n = counts[t.key] ?? 0;
        const label = n > 0 ? `${t.label}, ${formatNumber(n)} new` : quiet[t.key] ? `${t.label}, new` : undefined;
        return (
          <Link key={t.key} to={t.to} className={`tabbar__cell ${on ? "tabbar__cell--active" : ""}`} aria-current={on ? "page" : undefined} aria-label={label}>
            <span className="tabbar__disc"><Icon name={t.icon} size={on ? 22 : 24} stroke={on ? 2.2 : 2} /></span>
            {n > 0 ? <span className="tabbar__count" aria-hidden="true" data-testid={`tab-count-${t.key}`}>{n > 99 ? "99+" : formatNumber(n)}</span>
              : quiet[t.key] && <span className="tabbar__dot" aria-hidden="true" data-testid={`tab-dot-${t.key}`} />}
            <span className="tabbar__label">{t.label}</span>
          </Link>
        );
      })}
      <span className="home-indicator" aria-hidden="true" />
    </nav>
    </>
  );
}
